/**
 * DNS Records — full CRUD over a managed-DNS zone's records (RRSets). Pick a zone,
 * then list/search/filter records and create / edit / delete them. Mirrors the
 * SSL / Custom Pages list chrome (paginated DataTable + per-row action cell).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Network, Search, RotateCcw, Plus, Pencil, Trash2, Copy, Settings2, Server } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, Pagination, type ColumnDef } from '@vnetwork/ui/data';
import {
  Input,
  Badge,
  Skeleton,
  Button,
  IconButton,
  Switch,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Tooltip,
  TooltipProvider,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useWafDnsRecords,
  useWafDnsRecordTypes,
  usePatchWafDnsRecord,
  useDeleteWafDnsRecord,
  useDeleteDnsZone,
  useWafNameservers,
  wafExtractError,
  type WafDnsRecord,
} from '@/api/waf';
import { trackFeatureAction } from '@/lib/analytics-events';
import { useDebounce } from '../../shared';
import { WafScopeBar, type WafScope } from '../analytics/WafScopeBar';
import { DnsRecordFormDialog } from './DnsRecordFormDialog';
import styles from './WafDnsRecordList.module.css';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 50;

const muted = (v: React.ReactNode) => <span style={{ color: 'var(--vn-fg-muted)' }}>{v}</span>;
const mono = { fontFamily: 'var(--vn-font-mono)' } as const;

const num = (n?: number) => (n === undefined || n === null ? '' : String(n));

/**
 * Display name for a record. SRV records carry their service/protocol as separate
 * selectors, but the canonical FQDN prefixes them — e.g. name "vydevtest28.com"
 * with service "_sip" / protocol "_udp" shows as "_sip._udp.vydevtest28.com".
 */
function displayRecordName(record: WafDnsRecord): string {
  if (record.type === 'SRV' && record.service && record.protocol) {
    return `${record.service}.${record.protocol}.${record.name}`;
  }
  return record.name;
}

/**
 * Human-readable values for the table, using the structured `details` where a
 * flat string can't carry the whole record (SRV/MX/CAA); falls back to `values`.
 *   SRV → "priority weight port target" (e.g. "10 10 6688 target.example.app")
 *   MX  → "preference host"
 *   CAA → "flags tag value"
 */
function displayValues(record: WafDnsRecord): string[] {
  const details = record.details;
  if (details?.length) {
    if (record.type === 'SRV')
      return details.map((v) => `${num(v.priority)} ${num(v.weight)} ${num(v.port)} ${v.target ?? ''}`.trim());
    if (record.type === 'MX')
      return details.map((v) => `${num(v.preference)} ${v.value ?? ''}`.trim());
    if (record.type === 'CAA')
      return details.map((v) => `${num(v.flags)} ${v.tag ?? ''} ${v.value ?? ''}`.trim());
  }
  return record.values ?? [];
}

/** Record values, truncated to a couple chips + a `+N` overflow. */
function valuesCell(values: string[] | undefined, max = 2) {
  const items = values ?? [];
  if (items.length === 0) return muted('—');
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, ...mono }}>
      {items.slice(0, max).join(', ')}
      {items.length > max ? muted(` +${items.length - max}`) : null}
    </span>
  );
}

/** Multi-CDN toggle — reflects `proxied`; flipping PATCHes `{ proxied }`. */
function MultiCdnSwitchCell({
  serviceId,
  zoneId,
  record,
}: {
  serviceId: string;
  zoneId: string;
  record: WafDnsRecord;
}) {
  const { show } = useToast();
  const [on, setOn] = useState(record.proxied);
  // Re-sync from the (refetched) row whenever its proxied value changes.
  useEffect(() => setOn(record.proxied), [record.proxied]);

  const patch = usePatchWafDnsRecord(serviceId, {
    onSuccess: (_data, vars) => {
      show({ tone: 'success', title: vars.patch.proxied ? 'Multi-CDN enabled' : 'Multi-CDN disabled' });
      trackFeatureAction('waf', 'dns_record', 'toggle');
    },
    onError: (e) => {
      setOn(record.proxied); // revert optimistic flip
      show({ tone: 'danger', title: wafExtractError(e, 'Update failed') });
    },
  });

  return (
    <div onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex' }}>
      <Switch
        checked={on}
        aria-label="Multi-CDN"
        disabled={!record.proxiable || patch.isPending}
        onCheckedChange={(v) => {
          setOn(v);
          patch.mutate({ zoneId, id: record.id, patch: { proxied: v } });
        }}
      />
    </div>
  );
}

/** Row actions: edit (editable records) + delete (deletable records). */
function DnsRecordActionsCell({
  serviceId,
  zoneId,
  zoneName,
  record,
}: {
  serviceId: string;
  zoneId: string;
  zoneName?: string;
  record: WafDnsRecord;
}) {
  const { show } = useToast();
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const del = useDeleteWafDnsRecord(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'DNS record deleted' }); trackFeatureAction('waf', 'dns_record', 'delete'); setConfirmDelete(false); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Delete failed') }),
  });

  const copyCname = () => {
    if (!record.cdn_cname) return;
    void navigator.clipboard.writeText(record.cdn_cname);
    show({ tone: 'success', title: 'CNAME copied' });
  };
  const manageMultiCdn = () =>
    navigate(`/services/waf/${serviceId}/domains/view?domain=${encodeURIComponent(record.name)}`);

  return (
    <div onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', gap: 2 }}>
      <TooltipProvider>
        {record.cdn_cname && (
          <Tooltip content="Copy CNAME" side="top">
            <IconButton variant="ghost" size="sm" aria-label="Copy CNAME" onClick={copyCname}>
              <Copy size={14} />
            </IconButton>
          </Tooltip>
        )}
        {record.proxiable && (
          <Tooltip content="Manage Multi-CDN" side="top">
            <IconButton variant="ghost" size="sm" aria-label="Manage Multi-CDN" onClick={manageMultiCdn}>
              <Settings2 size={14} />
            </IconButton>
          </Tooltip>
        )}
        {record.editable && (
          <Tooltip content={record.proxied ? 'Edit origin address in domain settings' : 'Edit'} side="top">
            <IconButton
              variant="ghost"
              size="sm"
              aria-label="Edit DNS record"
              // Multi-CDN (proxied) records are edge-managed: their origin is
              // edited on the domain-detail page, not via the record form.
              onClick={() => (record.proxied ? manageMultiCdn() : setEditOpen(true))}
            >
              <Pencil size={14} />
            </IconButton>
          </Tooltip>
        )}
        {record.deletable && (
          <Tooltip content="Delete" side="top">
            <IconButton variant="ghost" size="sm" aria-label="Delete DNS record" onClick={() => setConfirmDelete(true)}>
              <Trash2 size={14} />
            </IconButton>
          </Tooltip>
        )}
      </TooltipProvider>

      {confirmDelete && (
        <ConfirmDialog
          open
          onOpenChange={(o) => { if (!o) setConfirmDelete(false); }}
          title="Delete DNS record?"
          description={`Delete the ${record.type} record "${record.name}"? This cannot be undone.`}
          confirmLabel="Delete"
          destructive
          loading={del.isPending}
          onConfirm={() => del.mutate({ zoneId, id: record.id })}
        />
      )}

      {editOpen && (
        <DnsRecordFormDialog
          serviceId={serviceId}
          zoneId={zoneId}
          zoneName={zoneName}
          record={record}
          open={editOpen}
          onOpenChange={setEditOpen}
        />
      )}
    </div>
  );
}

/**
 * Delegation nameservers for this managed-DNS zone. Rendered above the records
 * table so the customer can copy the hosts to point their registrar's NS at.
 */
function NameserversPanel({ serviceId }: { serviceId: string }) {
  const { show } = useToast();
  const { data: nameservers, isLoading } = useWafNameservers(serviceId);

  const copy = (text: string, label: string) => {
    void navigator.clipboard.writeText(text);
    show({ tone: 'success', title: `${label} copied` });
  };

  if (isLoading) return <Skeleton style={{ height: 52, marginBottom: 'var(--vn-space-4)' }} />;
  const hosts = nameservers ?? [];
  if (!hosts.length) return null;

  return (
    <div
      style={{
        marginBottom: 'var(--vn-space-4)',
        padding: '4px var(--vn-space-2)',
        border: '1px solid var(--vn-primary-solid)',
        borderRadius: 'var(--vn-radius-md)',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--vn-space-2)',
        flexWrap: 'wrap',
        maxWidth: '100%',
      }}
    >
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
        <Server size={15} color="var(--vn-primary-solid)" />
        <span style={{ fontWeight: 'var(--vn-font-weight-medium)', fontSize: 'var(--vn-font-size-sm)' }}>Nameservers</span>
        <TooltipProvider>
          <Tooltip
            content="At your domain registrar, replace your current nameservers with these hosts to delegate DNS to us. Delegation may take some time to propagate."
            side="top"
          >
            <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)', cursor: 'help' }}>ⓘ</span>
          </Tooltip>
        </TooltipProvider>
      </div>
      {hosts.map((ns) => (
        <span
          key={ns.host}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '1px 4px 1px 8px',
            border: '1px solid var(--vn-border-subtle)',
            borderRadius: 'var(--vn-radius-md)',
            fontSize: 'var(--vn-font-size-sm)',
          }}
        >
          <span style={{ ...mono }}>{ns.host}</span>
          <IconButton variant="ghost" size="sm" aria-label={`Copy ${ns.host}`} onClick={() => copy(ns.host, 'Nameserver')}>
            <Copy size={12} />
          </IconButton>
        </span>
      ))}
    </div>
  );
}

export function WafDnsRecordList({ serviceId }: { serviceId: string }) {
  const { data: recordTypes } = useWafDnsRecordTypes(serviceId);
  const { show } = useToast();

  const [zoneId, setZoneId] = useState('');
  const [zoneName, setZoneName] = useState<string | undefined>();
  const [confirmDeleteZone, setConfirmDeleteZone] = useState(false);
  // Type-to-confirm: the user must type the site name to enable deletion.
  const [deleteZoneInput, setDeleteZoneInput] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 300);
  const [createOpen, setCreateOpen] = useState(false);

  // Zone selection is owned by the shared WafScopeBar (zone mode, no "All site").
  // It persists the choice and defaults to the first usable zone.
  const onScope = (s: WafScope) => {
    const id = s.zone?.id ?? '';
    if (id !== zoneId) setPage(1);
    setZoneId(id);
    setZoneName(s.zone?.domain);
  };

  const deleteZone = useDeleteDnsZone(serviceId, {
    onSuccess: () => {
      // The zone list refetches and WafScopeBar re-selects the next usable zone.
      show({ tone: 'success', title: 'Site deleted' });
      trackFeatureAction('waf', 'domain', 'delete');
      setConfirmDeleteZone(false);
    },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Delete failed') }),
  });

  const { data, isLoading, isError, isFetching, refetch } = useWafDnsRecords(serviceId, zoneId, {
    page,
    page_size: pageSize,
    type: typeFilter === 'all' ? undefined : typeFilter,
    keyword: debouncedSearch.trim() || undefined,
  });

  const rows = data?.items ?? [];
  const total = data?.pagination?.total ?? 0;
  const pageCount = Math.max(1, data?.pagination?.total_pages ?? 1);

  const columns = useMemo<ColumnDef<WafDnsRecord, unknown>[]>(
    () => [
      {
        id: 'type',
        header: 'Type',
        enableSorting: false,
        cell: ({ row }) => (
          <Badge tone="neutral" appearance="subtle" size="sm">{row.original.type}</Badge>
        ),
      },
      {
        id: 'name',
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 500, ...mono }}>
            <Network size={14} color="var(--vn-fg-muted)" />
            {displayRecordName(row.original) || muted('—')}
          </span>
        ),
      },
      {
        id: 'values',
        header: 'Values',
        enableSorting: false,
        cell: ({ row }) => valuesCell(displayValues(row.original)),
      },
      {
        id: 'ttl',
        header: 'TTL',
        enableSorting: false,
        // Proxied (Multi-CDN) records are edge-managed — TTL is Auto, not the stored value.
        cell: ({ row }) => muted(row.original.proxied ? 'Auto' : row.original.ttl),
      },
      {
        id: 'multi-cdn',
        header: 'Multi-CDN',
        enableSorting: false,
        cell: ({ row }) => (
          <MultiCdnSwitchCell serviceId={serviceId} zoneId={zoneId} record={row.original} />
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => (
          <DnsRecordActionsCell serviceId={serviceId} zoneId={zoneId} zoneName={zoneName} record={row.original} />
        ),
      },
    ],
    [serviceId, zoneId, zoneName],
  );

  const hasZone = !!zoneId;
  const hasServerFilter = typeFilter !== 'all' || !!debouncedSearch.trim();

  return (
    <Container size="xl">
      <PageHeader
        icon={<Network size={22} />}
        title={`DNS Records${total ? ` (${total})` : ''}`}
        description="Manage the DNS records of a managed-DNS zone."
      />

      <div className={styles.toolbar}>
        <div className={styles.searchGroup}>
          <WafScopeBar
            serviceId={serviceId}
            mode="zone"
            allowAllSites={false}
            storeKey={`waf:${serviceId}:dns-records:scope`}
            onChange={onScope}
            className={styles.zoneScope}
          />
          <Input
            wrapperClassName={styles.search}
            placeholder="Search by name"
            startSlot={<Search />}
            value={searchInput}
            onChange={(e) => { setSearchInput(e.target.value); setPage(1); }}
            disabled={!hasZone}
          />
          <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setPage(1); }} disabled={!hasZone}>
            <SelectTrigger aria-label="Filter by type" className={styles.typeFilter}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {(recordTypes ?? []).map((t) => (
                <SelectItem key={t.name} value={t.name}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className={styles.actions}>
          <Button
            variant="danger"
            startIcon={<Trash2 size={15} />}
            onClick={() => { setDeleteZoneInput(''); setConfirmDeleteZone(true); }}
            disabled={!hasZone}
            aria-label="Delete site"
          >
            Delete site
          </Button>
          <Button
            variant="secondary"
            startIcon={<RotateCcw size={15} />}
            onClick={() => refetch()}
            disabled={!hasZone}
            aria-label="Refresh DNS records list"
          >
            Refresh
          </Button>
          <Button startIcon={<Plus size={15} />} onClick={() => setCreateOpen(true)} disabled={!hasZone}>
            Add record
          </Button>
        </div>
      </div>

      {hasZone && <NameserversPanel serviceId={serviceId} />}

      {!hasZone ? (
        <EmptyState
          icon={<Network size={20} />}
          title="Select a zone"
          description="Pick a DNS zone above to view and manage its records."
        />
      ) : isLoading ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} style={{ height: 48 }} />
          ))}
        </div>
      ) : isError ? (
        <Alert
          tone="danger"
          title="Couldn't load DNS records"
          actions={<button onClick={() => refetch()}>Retry</button>}
        >
          The WAF service couldn't return this zone's DNS records. Try again.
        </Alert>
      ) : total === 0 && !hasServerFilter ? (
        <EmptyState
          icon={<Network size={20} />}
          title="No DNS records yet"
          description="Add a record to this zone to get started."
          actions={
            <Button startIcon={<Plus size={15} />} onClick={() => setCreateOpen(true)}>
              Add record
            </Button>
          }
        />
      ) : (
        <>
          <DataTable
            columns={columns}
            data={rows}
            getRowId={(r) => r.id}
            emptyState="No DNS records match your search or filter."
            footer={`Showing ${rows.length ? (page - 1) * pageSize + 1 : 0}–${(page - 1) * pageSize + rows.length} of ${total}`}
          />
          {(pageCount > 1 || PAGE_SIZE_OPTIONS.length > 1) && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 'var(--vn-space-4)',
                marginTop: 'var(--vn-space-4)',
                flexWrap: 'wrap',
              }}
            >
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
                <label
                  htmlFor="waf-dns-page-size"
                  style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}
                >
                  Rows per page
                </label>
                <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
                  <SelectTrigger id="waf-dns-page-size" size="sm" style={{ width: 72 }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZE_OPTIONS.map((s) => (
                      <SelectItem key={s} value={String(s)}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {pageCount > 1 && (
                <Pagination
                  page={page}
                  pageCount={pageCount}
                  onPageChange={setPage}
                  summary={isFetching ? 'Loading…' : `Page ${page} of ${pageCount}`}
                />
              )}
            </div>
          )}
        </>
      )}

      {createOpen && hasZone && (
        <DnsRecordFormDialog
          serviceId={serviceId}
          zoneId={zoneId}
          zoneName={zoneName}
          open={createOpen}
          onOpenChange={setCreateOpen}
        />
      )}

      {confirmDeleteZone && hasZone && (
        <Dialog open onOpenChange={(o) => { if (!o) setConfirmDeleteZone(false); }}>
          <DialogContent size="md">
            <DialogHeader>
              <DialogTitle>Delete this site?</DialogTitle>
              <DialogDescription>
                This permanently deletes the site and all of its DNS records and configuration.
                This cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogBody style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
              <span style={{ fontSize: 'var(--vn-font-size-sm)' }}>
                To confirm, type the site name{' '}
                <strong style={{ ...mono }}>{zoneName}</strong> below.
              </span>
              <Input
                placeholder={zoneName}
                value={deleteZoneInput}
                onChange={(e) => setDeleteZoneInput(e.target.value)}
                disabled={deleteZone.isPending}
                autoFocus
                aria-label="Confirm site name"
              />
            </DialogBody>
            <DialogFooter>
              <div style={{ flex: 1 }} />
              <Button variant="secondary" onClick={() => setConfirmDeleteZone(false)} disabled={deleteZone.isPending}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => deleteZone.mutate({ zoneId })}
                disabled={deleteZone.isPending || !zoneName || deleteZoneInput.trim() !== zoneName}
              >
                {deleteZone.isPending ? 'Deleting…' : 'Delete site'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Container>
  );
}

export default WafDnsRecordList;
